'use client';
import { useMemo, useState } from 'react';

import { AccordionInterface, AccordionItemInterface } from '@veneer/core';
import { withTheme, Theme as VeneerTheme } from '@veneer/theme';

import styles from './accordion.module.scss';
import AccordionContext from './accordionContext';
import AccordionItem from './accordionItem';
import ItemProps from './ItemProps';

export type Theme = VeneerTheme;

export type AccordionEvents = (
  event?: React.UIEvent,
  index?: number,
  item?: AccordionItemInterface
) => void;

export interface AccordionProps extends AccordionInterface {
  items?: Array<AccordionItemInterface>;
  onCollapse?: AccordionEvents;
  onExpand?: AccordionEvents;
  theme?: Theme;
  singleExpandedItem?: number;
  customAppearance?: 'plus' | 'chevron';
  style?: string;
  backgroundColor?: string;
  textColor?: string;
  hideBorder?: boolean;
}

function getInitialExpandedItem(items: Array<ItemProps>) {
  const expandedIndex = items.reduce((array: Array<number>, item, index) => {
    if (item.expanded) {
      return array.concat(index);
    }
    return array;
  }, []);

  return expandedIndex[0];
}

export const Accordion = (props: AccordionProps) => {
  const {
    behavior,
    id: accordionID,
    items,
    onCollapse,
    onExpand,
    theme,
    style = 'item',
    customAppearance,
    backgroundColor,
    textColor,
    hideBorder,
    ...AccordionProps
  } = props;

  const hasSingleBehavior = behavior === 'singleExpand';
  const [singleExpandedItem, setSingleExpandedItem] = useState<
    number | undefined
  >(getInitialExpandedItem(items || []));

  return (
    <AccordionContext.Provider
      value={useMemo(
        () => ({
          items,
          onCollapse,
          onExpand,
          theme,
          style,
          singleExpandedItem,
          customAppearance,
          backgroundColor,
          textColor,
          hideBorder,
        }),
        [items, singleExpandedItem]
      )}
    >
      <div
        data-allow-multiple={!hasSingleBehavior}
        data-allow-toggle={hasSingleBehavior}
        id={accordionID}
        {...AccordionProps}
        className={styles['container']}
      >
        {items?.map((item, index) => (
          <AccordionItem
            appearance={props.customAppearance}
            customStyle={style}
            hasSingleBehavior={hasSingleBehavior}
            index={index}
            item={item}
            key={`accordion_item-${item.id}`}
            onSingleExpandChange={setSingleExpandedItem}
          />
        ))}
      </div>
    </AccordionContext.Provider>
  );
};

Accordion.displayName = 'Accordion';

export default withTheme(Accordion);
